import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Overview() {
  // SEO Configuration
  useSEO({
    title: 'Healthcare Network Overview - The Villarreal Health Network',
    description: 'Comprehensive overview of our healthcare network services, agencies, and resources. Explore skilled nursing, personal care, therapy services, and specialized medical support across Nevada.',
    keywords: 'healthcare network overview, home health services, skilled nursing agencies, personal care providers, therapy services Nevada',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": "Healthcare Network Overview",
          "description": "Comprehensive overview of The Villarreal Health Network services and agencies",
          "provider": getOrganizationSchema()
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Overview', url: '/overview' }
        ])
      ]
    }
  });

  const [activeSection, setActiveSection] = useState('network');

  const networkAgencies = [
    {
      name: 'Med-Care Providers',
      description: 'Primary & Mental Health Services',
      icon: 'ri-stethoscope-line',
      color: 'blue',
      services: ['Primary Care', 'Psychiatric Services', 'Pain Management', 'Wound Care', 'Home Visits'],
      link: '/compare'
    },
    {
      name: 'Celebrity Home Health',
      description: 'Skilled Nursing & Therapy',
      icon: 'ri-nurse-line',
      color: 'teal',
      services: ['Skilled Nursing', 'Physical Therapy', 'Occupational Therapy', 'Speech Therapy', 'IV Therapy'],
      link: '/compare'
    },
    {
      name: 'Accent Senior Care',
      description: 'Personal & Companion Care',
      icon: 'ri-user-heart-line',
      color: 'green',
      services: ['Personal Care', 'Companion Services', 'Light Housekeeping', 'Meal Preparation', 'Transportation'],
      link: '/compare'
    },
    {
      name: 'A Peace of Mind Caregivers',
      description: 'Memory Care Specialists',
      icon: 'ri-brain-line',
      color: 'purple',
      services: ['Alzheimer\'s Care', 'Memory Support', 'Respite Services', 'Cognitive Activities', 'Specialized Care'],
      link: '/compare'
    }
  ];

  const services = [
    {
      title: 'Request Care',
      description: 'Start your care journey with our comprehensive referral system',
      icon: 'ri-file-text-line',
      color: 'blue',
      link: '/referral',
      features: ['Quick Assessment', 'Insurance Verification', 'Care Matching', 'Fast Response']
    },
    {
      title: 'Emergency Support',
      description: '24/7 response available for urgent healthcare needs',
      icon: 'ri-alarm-warning-line',
      color: 'red',
      link: '/contact',
      features: ['Immediate Response', 'STAT Referrals', 'Crisis Support', 'Emergency Coordination']
    },
    {
      title: 'Insurance Verification',
      description: 'Check coverage options and understand your benefits',
      icon: 'ri-shield-check-line',
      color: 'green',
      link: '/contact',
      features: ['Coverage Analysis', 'Benefit Explanation', 'Prior Authorization', 'Claims Support']
    }
  ];

  const professionalServices = [
    {
      title: 'Hospital Discharge Planning',
      description: 'Seamless transitions from hospital to home care',
      icon: 'ri-hospital-line',
      benefits: ['Reduced Readmissions', 'Coordinated Care', 'Quick Placement', 'Quality Assurance']
    },
    {
      title: 'Case Management Support',
      description: 'Professional resources for case managers and social workers',
      icon: 'ri-user-settings-line',
      benefits: ['Provider Network', 'Quality Metrics', 'Outcome Tracking', 'Documentation Support']
    },
    {
      title: 'Physician Referrals',
      description: 'Direct referral system for healthcare providers',
      icon: 'ri-user-add-line',
      benefits: ['Electronic Referrals', 'Status Updates', 'Care Coordination', 'Outcome Reports']
    }
  ];

  const resources = [
    {
      category: 'Care Guides',
      description: 'Educational materials for patients and families',
      icon: 'ri-book-open-line',
      items: ['Home Health Guide', 'Medication Management', 'Safety Checklists', 'Care Planning']
    },
    {
      category: 'Insurance Information',
      description: 'Coverage details and billing information',
      icon: 'ri-file-shield-line',
      items: ['Medicare Coverage', 'Medicaid Waivers', 'Private Insurance', 'Payment Options']
    },
    {
      category: 'FAQ & Support',
      description: 'Common questions and support resources',
      icon: 'ri-question-answer-line',
      items: ['Service Questions', 'Billing Inquiries', 'Care Concerns', 'Technical Support']
    }
  ];

  const contactOptions = [
    {
      method: 'Phone Support',
      description: 'Speak directly with our care coordinators',
      icon: 'ri-phone-line',
      contact: '(702) 555-1234',
      availability: 'Mon-Fri 8AM-6PM, Sat 9AM-2PM'
    },
    {
      method: 'Emergency Line',
      description: 'Urgent care needs and after-hours support',
      icon: 'ri-alarm-warning-line',
      contact: '(702) 555-STAT',
      availability: '24/7 Emergency Response'
    },
    {
      method: 'Email Support',
      description: 'Non-urgent questions and information requests',
      icon: 'ri-mail-line',
      contact: 'info@villarrealhealth.com',
      availability: 'Response within 4 hours'
    },
    {
      method: 'Live Chat',
      description: 'Instant help with care coordination',
      icon: 'ri-customer-service-line',
      contact: 'Chat Now',
      availability: 'Business hours only'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-teal-600 to-blue-700 text-white py-24 pt-36 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/a548ec0d84da5c5423a9da4db9699067.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                Complete Healthcare Solutions
              </span>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-8">
              Everything You Need to Know
            </h1>
            <p className="text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Explore our comprehensive healthcare network, services, resources, and support options. Find everything you need in one place.
            </p>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50 sticky top-16 z-40 border-b-2 border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { id: 'network', name: 'Our Network', icon: 'ri-hospital-line' },
              { id: 'services', name: 'Services', icon: 'ri-service-line' },
              { id: 'professionals', name: 'For Professionals', icon: 'ri-user-settings-line' },
              { id: 'resources', name: 'Resources', icon: 'ri-book-open-line' },
              { id: 'contact', name: 'Contact', icon: 'ri-customer-service-line' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center px-8 py-4 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap text-lg shadow-lg transform hover:scale-105 ${
                  activeSection === tab.id
                    ? 'bg-blue-600 text-white shadow-xl'
                    : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-blue-300'
                }`}
              >
                <i className={`${tab.icon} mr-3 text-xl`}></i>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Our Network Section */}
      {activeSection === 'network' && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Our Healthcare Network</h2>
              <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
                Four specialized agencies working together to provide comprehensive home-based healthcare services
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {networkAgencies.map((agency, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-2xl p-12 hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100">
                  <div className="flex items-center mb-8">
                    <div className={`w-20 h-20 bg-gradient-to-br from-${agency.color}-500 to-${agency.color}-600 rounded-3xl flex items-center justify-center mr-6 shadow-xl`}>
                      <i className={`${agency.icon} text-white text-3xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-2">{agency.name}</h3>
                      <p className="text-gray-600 text-lg">{agency.description}</p>
                    </div>
                  </div>
                  
                  <div className="mb-10">
                    <h4 className="font-bold text-gray-900 mb-6 text-xl">Key Services:</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {agency.services.map((service, serviceIndex) => (
                        <div key={serviceIndex} className="flex items-center">
                          <i className="ri-check-line text-green-600 mr-4 text-lg bg-green-100 rounded-full p-1"></i>
                          <span className="text-gray-700 font-medium">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-8 border-t-2 border-gray-200">
                    <Link to={agency.link} className="text-blue-600 font-bold hover:text-blue-700 transition-colors duration-200 text-lg">
                      <i className="ri-information-line mr-2"></i>
                      Learn More
                    </Link>
                    <Link to="/referral">
                      <Button size="sm" className="whitespace-nowrap transform hover:scale-105 transition-all duration-200">
                        <i className="ri-arrow-right-line mr-2"></i>
                        Get Started
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-16">
              <Link to="/compare">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200 whitespace-nowrap text-xl px-12 py-5 shadow-xl">
                  <i className="ri-compare-line mr-3"></i>
                  Compare All Services
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      {activeSection === 'services' && (
        <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Our Services</h2>
              <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive healthcare services designed to meet your unique needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-2xl p-12 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className={`w-24 h-24 bg-gradient-to-br from-${service.color}-500 to-${service.color}-600 rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-xl`}>
                    <i className={`${service.icon} text-white text-4xl`}></i>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">{service.title}</h3>
                  <p className="text-gray-600 mb-10 text-lg text-center">{service.description}</p>
                  
                  <div className="mb-10">
                    <h4 className="font-bold text-gray-900 mb-6 text-xl">Features:</h4>
                    <div className="space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center">
                          <i className="ri-check-line text-green-600 mr-4 text-lg bg-green-100 rounded-full p-1"></i>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-center">
                    <Link to={service.link}>
                      <Button size="lg" className="w-full whitespace-nowrap transform hover:scale-105 transition-all duration-200">
                        <i className="ri-arrow-right-line mr-2"></i>
                        Access Service
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-20 bg-gradient-to-r from-blue-600 via-teal-600 to-blue-600 rounded-3xl p-16 text-center text-white">
              <h3 className="text-4xl font-bold mb-8">Need Immediate Help?</h3>
              <p className="text-2xl text-blue-100 mb-10 max-w-2xl mx-auto">
                Our emergency response team is available 24/7 for urgent healthcare needs
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a href="tel:+17025557828">
                  <Button variant="secondary" size="lg" className="whitespace-nowrap text-xl px-10 py-4">
                    <i className="ri-phone-line mr-3"></i>
                    Call Emergency Line
                  </Button>
                </a>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap cursor-pointer text-xl px-10 py-4"
                  onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                >
                  <i className="ri-customer-service-line mr-3"></i>
                  Live Chat Support
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* For Professionals Section */}
      {activeSection === 'professionals' && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-gray-900 mb-8">For Healthcare Professionals</h2>
              <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
                Professional resources and referral services for healthcare providers, case managers, and discharge planners
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {professionalServices.map((service, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-12 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border border-gray-200">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-xl">
                    <i className={`${service.icon} text-white text-3xl`}></i>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{service.title}</h3>
                  <p className="text-gray-600 mb-10 text-lg text-center">{service.description}</p>
                  
                  <div className="mb-10">
                    <h4 className="font-bold text-gray-900 mb-6 text-lg">Benefits:</h4>
                    <div className="space-y-3">
                      {service.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-center">
                          <i className="ri-check-line text-green-600 mr-3 text-lg bg-green-100 rounded-full p-1"></i>
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-20 bg-white rounded-3xl shadow-2xl p-16 border border-gray-100">
              <div className="text-center mb-12">
                <h3 className="text-4xl font-bold text-gray-900 mb-6">Professional Referral Process</h3>
                <p className="text-xl text-gray-600">Simple, efficient, and reliable referral system</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  { step: '1', title: 'Submit Referral', desc: 'Complete our professional referral form', icon: 'ri-file-text-line' },
                  { step: '2', title: 'Verification', desc: 'We verify insurance and eligibility', icon: 'ri-shield-check-line' },
                  { step: '3', title: 'Matching', desc: 'Match patient with appropriate agency', icon: 'ri-user-search-line' },
                  { step: '4', title: 'Coordination', desc: 'Coordinate care and provide updates', icon: 'ri-customer-service-line' }
                ].map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      <i className={`${step.icon} text-white text-2xl`}></i>
                    </div>
                    <div className="text-3xl font-bold text-blue-600 mb-4">Step {step.step}</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h4>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link to="/professionals">
                  <Button size="lg" className="whitespace-nowrap text-xl px-12 py-4 shadow-xl">
                    <i className="ri-user-settings-line mr-3"></i>
                    Access Professional Portal
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Resources Section */}
      {activeSection === 'resources' && (
        <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Healthcare Resources</h2>
              <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
                Educational materials, guides, and support resources to help you navigate healthcare services
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {resources.map((resource, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-2xl p-12 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-lg">
                    <i className={`${resource.icon} text-blue-600 text-3xl`}></i>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{resource.category}</h3>
                  <p className="text-gray-600 mb-10 text-lg text-center">{resource.description}</p>
                  
                  <div className="mb-10">
                    <h4 className="font-bold text-gray-900 mb-6 text-lg">Available Resources:</h4>
                    <div className="space-y-3">
                      {resource.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center">
                          <i className="ri-file-line text-blue-600 mr-3 text-lg bg-blue-100 rounded-full p-1"></i>
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-20">
              <div className="bg-white rounded-3xl shadow-2xl p-16 border border-gray-100">
                <div className="text-center mb-12">
                  <h3 className="text-4xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
                  <p className="text-xl text-gray-600">Quick answers to common healthcare questions</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {[
                    { q: 'How do I qualify for home health services?', a: 'Qualification depends on medical condition, insurance coverage, and physician orders.' },
                    { q: 'What insurance do you accept?', a: 'We accept Medicare, Medicaid, most private insurances, and private pay options.' },
                    { q: 'How quickly can services start?', a: 'Routine referrals start within 48 hours, urgent cases within 24 hours.' },
                    { q: 'Do you provide 24/7 care?', a: 'Yes, we offer 24/7 on-call services and can arrange overnight care when needed.' }
                  ].map((faq, index) => (
                    <div key={index} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-4 text-lg">{faq.q}</h4>
                      <p className="text-gray-600">{faq.a}</p>
                    </div>
                  ))}
                </div>

                <div className="text-center mt-12">
                  <Link to="/resources">
                    <Button size="lg" className="whitespace-nowrap text-xl px-12 py-4 shadow-xl">
                      <i className="ri-book-open-line mr-3"></i>
                      View All Resources
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      {activeSection === 'contact' && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Contact Our Care Team</h2>
              <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
                Multiple ways to reach us for immediate assistance with your healthcare needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
              {contactOptions.map((option, index) => (
                <div key={index} className="bg-white rounded-3xl shadow-2xl p-10 text-center hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl">
                    <i className={`${option.icon} text-white text-3xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{option.method}</h3>
                  <p className="text-gray-600 mb-6 text-lg">{option.description}</p>
                  <div className="mb-6">
                    {option.contact === 'Chat Now' ? (
                      <button
                        onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                        className="text-blue-600 font-bold text-xl hover:text-blue-700 transition-colors duration-200 cursor-pointer"
                      >
                        {option.contact}
                      </button>
                    ) : option.contact.includes('@') ? (
                      <a
                        href={`mailto:${option.contact}`}
                        className="text-blue-600 font-bold text-xl hover:text-blue-700 transition-colors duration-200"
                      >
                        {option.contact}
                      </a>
                    ) : (
                      <a
                        href={`tel:${option.contact.replace(/[^0-9]/g, '')}`}
                        className="text-blue-600 font-bold text-xl hover:text-blue-700 transition-colors duration-200"
                      >
                        {option.contact}
                      </a>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm">{option.availability}</p>
                </div>
              ))}
            </div>

            <div className="mt-20">
              <div className="bg-gradient-to-r from-blue-600 via-teal-600 to-blue-600 rounded-3xl p-16 text-center text-white">
                <h3 className="text-4xl font-bold mb-8">Office Locations</h3>
                <p className="text-2xl text-blue-100 mb-12 max-w-2xl mx-auto">
                  Serving families across Nevada with multiple convenient locations
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[
                    { city: 'Las Vegas', address: '3755 Breakthrough Way', phone: '(702) 555-1234' },
                    { city: 'Reno', address: '855 W Seventh St', phone: '(775) 555-1234' },
                    { city: 'Henderson', address: '2620 Regatta Dr', phone: '(702) 555-5678' }
                  ].map((location, index) => (
                    <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                      <h4 className="text-2xl font-bold mb-4">{location.city} Office</h4>
                      <p className="text-blue-100 mb-4">{location.address}</p>
                      <a href={`tel:${location.phone}`} className="text-white font-semibold text-lg hover:text-blue-200 transition-colors">
                        {location.phone}
                      </a>
                    </div>
                  ))}
                </div>

                <div className="mt-12">
                  <Link to="/contact">
                    <Button variant="secondary" size="lg" className="whitespace-nowrap text-xl px-12 py-4">
                      <i className="ri-map-pin-line mr-3"></i>
                      View All Locations
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-teal-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-8">
            Ready to Get Started?
          </h2>
          <p className="text-2xl text-blue-100 mb-12 max-w-3xl mx-auto">
            Let us help you find the perfect healthcare solution for your family. Our intake coordinators are standing by to assist you.
          </p>
          <div className="flex flex-col sm:flex-row gap-8 justify-center">
            <Link to="/referral">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto whitespace-nowrap text-xl px-12 py-5 shadow-xl transform hover:scale-105 transition-all duration-200">
                <i className="ri-file-text-line mr-3"></i>
                Submit a Referral
              </Button>
            </Link>
            <a href="tel:+17025551234">
              <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 border-2 border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap text-xl px-12 py-5 shadow-xl transform hover:scale-105 transition-all duration-200">
                <i className="ri-phone-line mr-3"></i>
                Call Now: (702) 555-1234
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
